import { memo, useEffect, useRef, useState } from 'react';

import { isLiveOpen } from '@apm/contracts';
import type { LineupItem, LineupSpan, LiveEventSlot, OnairSlot, PlaylistItem } from '@apm/contracts';
import { formatClock, formatTimeOfDay } from '@apm/i18n';
import type { Translator } from '@apm/i18n';

import {
  EPG_TRACK_WIDTH_PX,
  boxDensity,
  boxGeometry,
  hiddenUnderHeadPx,
  onairNowIndex,
  onairSpansAt,
  rowSpans,
  liveAdStripes,
  liveSpansAt,
  nextEpgAnchor,
  shiftSpans,
  tickTimes,
  xOf,
} from '../epg-grid.js';
import { useEpgAdsVisible } from '../ads/epg-ads-visible.js';
import { channelBadgeKey } from '../channel-badge.js';
import type { EpgAnchor, EpgLiveSpan, EpgSpan } from '../epg-grid.js';
import type { EpgRow } from '../epg.js';
import {
  applyCurrentRow,
  episodeNoOf,
  isFavorite,
  partitionByFavorite,
  showsSignInHint,
} from '../epg.js';
import type { MessageKey } from '../i18n/ko.js';
import { useLocale, useT } from '../i18n/LocaleProvider.js';

/**
 * 시각축 채널 편성표 (v27, `docs/55` · 절대 규칙 73). **v25 의 카드 행을 대체한다.**
 *
 * **`/` 를 이것으로 바꾸지 않았다** (v9 D8 승계). 여기는 `/channels` 이고, `/` 는 여전히
 * 시청 화면이다 — 하네스 **열둘**이 `/?debug=1` 에서 재생을 시작하고 그중 둘이 `T_freeze` 를
 * 재기 때문이다.
 *
 * ── 가로가 시간이다 ─────────────────────────────────────────
 *
 * 눈금은 **기기의 시계**이고 **화면이** 만든다 — 서버 응답에는 시각이 없다. 좌표는 전부
 * 「지금으로부터 몇 초」이고(`epg-grid.ts`), 그래서 **「지금」 선은 안 움직이고 눈금은
 * 흐른다.** 분기가 아니라 정의로 갈린다.
 *
 * ★ **흐르는 행은 재생 중인 하나뿐이다.** 안 보는 채널의 행은 「지금」 선에 붙어 있고,
 * 그 사실은 **「지금」 선 왼쪽이 비어 있는 것**으로 드러난다. 다 흐르게 만들면 화면은
 * 더 방송처럼 보이는데 **아무도 안 튼 채널의 콘텐츠가 지나가 버린 것처럼 그려진다.**
 *
 * ── 새 포커스 층을 만들지 않는다 ────────────────────────────
 *
 * 행에도 스크롤러에도 `data-focus-scope` 를 붙이지 않는다 (절대 규칙 65). 시트판의 층은
 * `ChannelSheet` 가 갖고, 전체화면판은 기본 층이다.
 */
export function EpgScreen({
  rows,
  currentChannelId,
  currentProductId,
  inLive = false,
  favorites,
  onToggleFavorite,
  onPick,
  signedIn,
  onBack,
  bare = false,
  now = null,
}: {
  rows: readonly EpgRow[];
  /** 지금 보고 있는 채널. 없으면 첫 진입(전체화면)이다 */
  currentChannelId: string | null;
  /** 지금 보고 있는 항목. 시트에서만 값이 있다 */
  currentProductId: string | null;
  /**
   * v33-c. **지금 그 채널이 바이패스인가**(`receiver.getLive() !== null`).
   *
   * 혼합 행은 두 칸이고 **둘 다 「지금」 선을 지난다** — 어느 쪽을 보고 있는지는 상자의
   * 자리로는 갈리지 않는다(둘 다 참이니까). 그래서 **보고 있지 않은 칸을 죽인다.**
   */
  inLive?: boolean;
  favorites: readonly string[];
  onToggleFavorite: (channelId: string) => void;
  /** `isEntryCard` 는 그 행의 **「지금」 상자**인가 — 판정은 `resolveEpgPick` 이 한다 */
  onPick: (channelId: string, productId: string, isEntryCard: boolean) => void;
  /** 로그인했는가. **「로그인하면 이어집니다」를 그릴지 가르는 값의 절반이다** */
  signedIn: boolean;
  /** 시청 화면으로 되돌아간다. 첫 진입에서는 없다 */
  onBack: (() => void) | null;
  /** 시트 안에 들어갈 때. 제목을 그리지 않는다 — 시트가 이미 갖고 있다 */
  bare?: boolean;
  /**
   * 지금 재생 중인 것에 대한 **사실**. 재생 전(게이트)이면 `null` 이다.
   *
   * ★ **화면이 사실만 받고 시계는 스스로 갖는다.** 부모(`WatchScreen`)에 시계를 심으면
   * 매 초 그쪽이 다시 그려지고, 그러면 다섯 행 백 개 상자가 초당 한 번 재조정된다 —
   * **화면은 멀쩡하고 폰에서만 더워진다** (`docs/55` §13 의 23).
   */
  now?: {
    /** 이것만 값이다 — `WatchScreen` 이 `key={channelId}` 로 못박혀 있어 바뀌지 않는다 */
    channelId: string;
    /**
     * ★★ **재생 상태는 값이 아니라 함수로 받는다** (2026-09-04에 값을 치르고 배웠다).
     *
     * `view` 는 렌더 시점의 스냅숏이고 **시트가 열려 있는 동안 `WatchScreen` 은 다시
     * 그려지지 않는다** — 시계를 이 화면이 스스로 갖기 때문이다. 그래서 값으로 받으면
     * **프리롤 중에 시트를 연 그 순간의 값이 영원히 얼어붙는다**: `inAdBreak` 은
     * `true` 로, `productId` 는 `null` 로. 둘 다 **앵커가 아예 안 잡히게** 만들고,
     * 증상은 **「재생 중인 행이 흐르지 않는다」** 하나다 (하네스가 `moved: [0,0,0,0,0]`
     * 으로 잡았다 · `useReceiver` 의 `getPlayhead` 주석).
     *
     * **`null` 이면 리시버가 아직 없다는 뜻이다** — 그때는 앵커를 잡지 않는다.
     */
    getPlayhead: () => {
      productId: string | null;
      elapsedSec: number;
      inAdBreak: boolean;
      /** v33. 바이패스 중인가 — 그때 `elapsedSec` 은 **라이브의 초**다 (`docs/67` D25) */
      inLive: boolean;
      /** v33-d. 바이패스에서 돌아갈 콘텐츠 초 — 그동안 녹화 칸은 이 초에서 멈춰 그려진다 */
      liveResumeAtSec: number | null;
      /** v35. 시청자가 멈췄는가 — 그동안 그 행은 흐르지 않는다(절대 규칙 73 ② · `docs/71` §2.6) */
      paused: boolean;
    } | null;
    /**
     * ★ v34. **지금 채널의 라이브 상자 — 리시버가 1초마다 갈아 끼운 창에서** (절대 규칙 87).
     *
     * 다른 행은 편성표를 열 때 받은 `lineup` 그대로다. 지금 채널만 이것으로 바꾸는 이유는 **큐가
     * 누르는 순간 바뀌는 값**이라서다 — 없으면 개시 큐를 받아 화면은 라이브인데 상자는 「개시
     * 대기」로 남는다. **주소가 없다**(`liveSlotsFromWindows` · 규칙 68). 없거나 `null` 이면 그대로.
     */
    getLiveSlots?: () => readonly LiveEventSlot[] | null;
    /**
     * v39. **리시버의 재생 목록** — 재생 중인 회차가 편성표에 없을 때 그 행을 이것으로 세운다(`applyCurrentRow` ·
     * `docs/81` §5.3). **값으로 받는다** — 목록은 고르기(시트가 닫힌다)와 바퀴 끝 재요청 때만 바뀌어, 시트가 열린 동안
     * 얼어붙어도 틀리지 않는다(`getPlayhead` 가 함수인 이유와 다르다).
     */
    items?: readonly PlaylistItem[];
  } | null;
}) {
  const t = useT();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);

  /*
   * 축의 기준. **이 화면이 스스로 갖는다** (위 `now` 의 ★).
   *
   * 매 초 다시 그려지는 것은 **눈금 열셋과 재생 중인 행 하나**뿐이다 — 안 보는 행은
   * 값이 안 변하므로 `memo` 가 언제나 적중한다 (`docs/55` §5.4).
   */
  const [nowMs, setNowMs] = useState(() => Date.now());
  /**
   * 포커스가 놓인 상자 (v27/TG19). **좁은 상자가 못 보여 준 것을 대신 말한다.**
   *
   * 홈쇼핑은 상품이 90~120초라 상자가 81~108px 이고, 그 안에서 읽히는 것은 「SIAA 레…」
   * 뿐이다. **폭을 늘려 푸는 길은 막혀 있다** — `min-width` 는 뒤를 전부 밀고(절대 규칙 73),
   * 배율을 올리면 보이는 시간이 준다. 그래서 **상자는 그대로 두고 글자만 다른 자리에
   * 편다.**
   *
   * ★ **툴바 줄을 쓴다.** 상자 위에 띄우면 옆 상자를 가리고, 그 층이 포커스 층과 겹치면
   * 규칙 65 가 막아 둔 자리가 다시 열린다. 툴바는 **이미 있는 줄**이라 세로 예산도
   * 안 든다 — 다섯 행이 한 화면에 들어가는 것이 이 확장의 요구다(`docs/55` §1).
   */
  const [focusedBox, setFocusedBox] = useState<{
    channelId: string;
    productId: string;
    when: string;
  } | null>(null);
  /** 재생 중인 행의 기준점. **뺄셈의 출발점이고, 다시 잡는 때는 순수 함수가 정한다** */
  const anchorRef = useRef<EpgAnchor | null>(null);
  const nowRef = useRef(now);
  nowRef.current = now;

  useEffect(() => {
    const tick = () => {
      const at = Date.now();
      /*
       * ★ **판정은 `nextEpgAnchor` 하나다** — 광고 중 재앵커 금지(TG14)·회차 교체·예측과의
       *   어긋남·**바이패스 중 멈춤**(v33-d)이 전부 거기 있고 node 테스트가 본다.
       *   `getElapsedSec()` 을 매 초 읽어 다시 세지 않는다(절대 규칙 73 ③).
       */
      anchorRef.current = nextEpgAnchor(
        anchorRef.current,
        nowRef.current?.getPlayhead() ?? null,
        at,
      );
      setNowMs(at);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(id);
    };
    // 시트가 닫히면(언마운트) 인터벌이 사라진다. 게이트에서는 `now` 가 `null` 이라 아무
    // 행도 안 흐르고 눈금만 간다.
  }, []);

  const ticks = tickTimes(nowMs);
  const anchor = anchorRef.current;
  /*
   * ★ **회전은 `applyCurrentRow` 하나다** — v25 가 쓰던 그 함수를 그대로 부른다
   * (`rotateToProduct` 가 구현이고, 편성표와 재생이 갈리지 않는 근거다 · 절대 규칙 68).
   * **옮긴 것이지 다시 쓴 것이 아니다** — `WatchScreen` 에서는 시트를 여는 순간 한 번만
   * 계산돼 **흐르지 않았다** (`docs/55` D14).
   */
  /*
   * v34. 지금 채널의 라이브 상자를 리시버의 새 창으로 바꾼다(위 `getLiveSlots` 의 ★). ONAIR 행은
   * 손대지 않는다 — 그 채널에는 라이브 이벤트가 없다.
   */
  const freshSlots = now?.getLiveSlots?.() ?? null;
  const withFreshLive =
    now == null || freshSlots === null
      ? rows
      : rows.map((row) =>
          row.channel.channelId === now.channelId && row.channel.delivery !== 'onair'
            ? { ...row, liveEvents: freshSlots }
            : row,
        );
  const shown =
    now == null || anchor === null
      ? withFreshLive
      : applyCurrentRow(withFreshLive, {
          channelId: now.channelId,
          productId: anchor.productId,
          elapsedSec: anchor.elapsedSec,
          ...(now.items === undefined ? {} : { items: now.items }),
        });
  /** 앵커를 잡은 뒤 흐른 벽시계. **왼쪽으로 흐르는 것이 이 한 값이다** */
  const driftSec = anchor === null ? 0 : (nowMs - anchor.atMs) / 1000;
  const { favorites: fav, rest } = partitionByFavorite(
    shown.map((row) => row.channel),
    favorites,
  );
  const byId = new Map(shown.map((row) => [row.channel.channelId, row]));
  /*
   * 포커스된 상자가 가리키는 채널과 항목. **`shown` 에서 되찾는다** — 상태에 제목을
   * 복사해 두면 편성표가 갱신됐을 때 **옛 제목이 남는다**(TG15 가 고친 그 종류).
   */
  const focusedRow = (() => {
    if (focusedBox === null) return null;
    const row = byId.get(focusedBox.channelId);
    const item = row?.lineup?.items.find((i) => i.productId === focusedBox.productId);
    return row === undefined || item === undefined ? null : { channel: row.channel, item };
  })();
  /*
   * 즐겨찾기 구획을 **같은 캔버스 안의 띠**로 둔다 — 스크롤러가 갈리면 시간축이 갈린다.
   * 즐겨찾기가 하나도 없으면 띠를 아예 안 그린다(구획이 하나뿐이면 이름이 필요 없다).
   */
  const cells: ({ kind: 'band'; key: MessageKey } | { kind: 'row'; row: EpgRow })[] = [];
  if (fav.length > 0) {
    cells.push({ kind: 'band', key: 'epg.favorites' });
    for (const channel of fav) {
      const row = byId.get(channel.channelId);
      if (row !== undefined) cells.push({ kind: 'row', row });
    }
    if (rest.length > 0) cells.push({ kind: 'band', key: 'epg.allChannels' });
  }
  for (const channel of rest) {
    const row = byId.get(channel.channelId);
    if (row !== undefined) cells.push({ kind: 'row', row });
  }

  if (rows.length === 0) {
    return (
      <div className={bare ? '' : 'card'}>
        <h2 className="card__title">{t('channels.emptyTitle')}</h2>
        <p className="card__text">{t('channels.emptyText')}</p>
      </div>
    );
  }

  // 판정은 순수 함수가 한다 — 여기서 조건을 쓰면 node 테스트에서 통째로 빠진다.
  const signInHint = showsSignInHint(rows, signedIn);
  const [showAds, setShowAds] = useEpgAdsVisible();

  return (
    <div className={bare ? '' : 'card'}>
      {!bare && <h2 className="card__title">{t('epg.title')}</h2>}

      <div
        className={`epg${showAds ? '' : ' epg--hide-ads'}`}
        data-epg-ads={showAds ? 'shown' : 'hidden'}
        style={{ ['--epg-row-count' as string]: String(rows.length) }}
      >
        {/* 안내와 「지금으로」를 한 줄에 둔다 — 세로 예산이 다섯 행에 걸려 있다 */}
        <div className="epg__toolbar">
          {/*
            v27/TG19. **포커스가 상자에 있으면 그 상자가 못 보여 준 것을 여기서 말한다.**
            없으면 원래의 안내다 — 줄이 하나이고 세로 예산이 안 는다.

            ★ **`labelLine` 을 그대로 쓴다** — 상자 안과 **같은 함수**여야 사전 키도
            `data-i18n` 표시도 갈리지 않는다(규칙 24·49). 다시 쓰면 홈쇼핑의 분류가
            번역 누락으로 신고되거나, 반대로 진짜 누락이 조용해진다.
          */}
          {focusedRow === null ? (
            <p className="epg__hint">{t('epg.gridHint')}</p>
          ) : (
            <p className="epg__hint epg__detail" data-testid="epg-detail">
              <span className="epg__detail-channel" data-i18n="data">
                {focusedRow.channel.name}
              </span>
              <span className="epg__detail-title" data-i18n="data">
                {focusedRow.item.title}
              </span>
              {labelLine(t, focusedRow.item, focusedRow.channel.kind === 'general')}
              <span className="epg__detail-when">{focusedBox?.when}</span>
            </p>
          )}
          {/*
            ★ **언제나 렌더한다** (D15). 스크롤했을 때만 그리면 **그 버튼 위에서 누르는
            순간 버튼이 사라져** 포커스가 `<body>` 로 떨어지고, 3m 밖에서 그것은
            「리모컨이 죽었다」다.
          */}
          {/*
            2026-10-29 · **광고 시간 표시** — 편성표의 광고 띠 전부(회차 사이·중간 · ONAIR CSPM · 라이브 앞뒤)를 함께 켜고 끈다(`docs/45` §18.7).
            체크박스가 아니라 `<button>` 이다(포커스 후보 · 리모컨) · 상태는 `aria-pressed`. 상자의 자리·폭은 그대로다.
          */}
          <button
            type="button"
            className="epg__to-now epg__ads-toggle"
            data-testid="epg-ads-toggle"
            aria-pressed={showAds}
            onClick={() => {
              setShowAds(!showAds);
            }}
          >
            {/* 띠의 견본 · 이름 · 스위치(켜짐 = 호박색 · 손잡이 오른쪽). 글로만 「표시/숨김」이면 버튼으로 안 읽혔다(2026-10-29 신고) */}
            <span className="epg__ads-swatch" aria-hidden="true" />
            <span>{t('epg.adsLabel')}</span>
            <span className="epg__switch" aria-hidden="true">
              <span className="epg__switch-knob" />
            </span>
            <span className="epg__ads-state">{t(showAds ? 'epg.adsOn' : 'epg.adsOff')}</span>
          </button>
          <button
            type="button"
            className="epg__to-now"
            data-testid="epg-to-now"
            onClick={() => {
              scrollRef.current?.scrollTo({ left: 0 });
            }}
          >
            {t('epg.backToNow')}
          </button>
        </div>

        {/*
          ★ **스크롤러가 하나다** (v25 의 「행마다 스크롤러다」를 되돌린 자리 · `docs/55` §7).
          행마다 따로 스크롤하면 **같은 x 가 행마다 다른 시각**이 되고, 그 순간 이 화면은
          편성표가 아니라 가로 목록 다섯 개다. 행머리가 화면 밖으로 나가는 문제는
          **고정 열**(`.epg__head { position: sticky }`)이 갚는다.
        */}
        <div
          className="epg__scroll"
          ref={scrollRef}
          /*
            ★ **포커스가 고정 열 아래로 숨는 것을 여기서 되돌린다** (`epg-grid.ts` 의
            `hiddenUnderHeadPx` 주석). CSS 만으로는 안 된다 — `scroll-padding` 은
            **스크롤이 일어날 때만** 듣고, 상자가 스크롤포트 안에 있으면 브라우저는
            「보인다」고 판단해 **아무것도 하지 않는다.**

            링이 안 보이면 3m 밖에서 그것은 **「리모컨이 죽었다」와 구분되지 않는다.**
            **판정은 순수 함수가 낸다** — 여기서 산술을 쓰면 node 테스트 밖으로 나간다.
          */
          onFocus={(event) => {
            const scroller = scrollRef.current;
            const box = event.target;
            if (scroller === null || !(box instanceof HTMLElement)) return;
            if (box.dataset['testid'] !== 'epg-card') {
              // 링이 상자를 떠났다 — 별·「지금으로」로 갔거나 층을 나갔다.
              setFocusedBox(null);
              return;
            }
            /*
             * v27/TG19. **여기서 잡는 이유는 자리가 하나이기 때문이다** — 상자마다
             * `onFocus` 를 달면 백 개에 핸들러가 백 개이고, `memo` 가 그만큼 자주 깨진다.
             * 이 핸들러는 이미 있고(고정 열 되돌림) **버블링으로 전부 지난다.**
             */
            setFocusedBox({
              channelId: box.dataset['channelId'] ?? '',
              productId: box.dataset['productId'] ?? '',
              // 「지금 재생 중」·「약 12분 후」·「12:03부터 이어보기」 — 이미 그린 값이다
              when: box.querySelector('[data-testid=epg-when]')?.textContent ?? '',
            });
            const head = scroller.querySelector('.epg__head');
            if (head === null) return;
            const hidden = hiddenUnderHeadPx({
              boxLeft: box.getBoundingClientRect().left,
              portLeft: scroller.getBoundingClientRect().left,
              headWidthPx: head.getBoundingClientRect().width,
            });
            if (hidden > 0) scroller.scrollLeft -= hidden;
          }}
        >
          <div className="epg__canvas" style={{ ['--epg-track-w' as string]: `${String(EPG_TRACK_WIDTH_PX)}px` }}>
            {/*
              ★ 눈금은 **캔버스에 직접** 놓는다. 축 안에 두고 세로선을 `100vh` 로 늘리면
              그 넘침이 스크롤러의 `scrollHeight` 를 부풀려 **다섯 행이 세로로 넘친다** —
              실제로 처음 그렇게 짰고 `scrollHeight 924 > clientHeight 586` 으로 잡혔다.
              캔버스에 놓으면 `top: 0; bottom: 0` 이 **캔버스 높이에 갇힌다.**

              ★★ 라벨과 세로선을 **같은 엘리먼트**가 낸다 (`docs/55` §9.3). 선을 배경
              그라디언트로 그리면 위치를 두 번 계산하게 되고, 어긋나면 「라벨이 선에서
              조금 비켰다」가 되는데 **작아서 아무도 신고하지 않는다.**
            */}
            <div className="epg__axis" role="group" aria-label={t('epg.timeAxis')}>
              <div className="epg__axis-head" aria-hidden="true" />
            </div>

            {ticks.map((tick) => (
              <span
                key={tick.epochMs}
                className="epg__tick"
                style={{ left: `calc(var(--epg-head-w) + ${String(xOf(tick.atSec))}px)` }}
                data-i18n="data"
              >
                {formatTimeOfDay(tick.epochMs, locale)}
              </span>
            ))}

            {/* 「지금」 선 — 상자 위, 고정 열 아래. `pointer-events` 를 갖지 않는다 */}
            <div
              className="epg__now-line"
              style={{ left: `calc(var(--epg-head-w) + ${String(xOf(0))}px)` }}
              aria-hidden="true"
            >
              <span className="epg__now-chip">{t('epg.nowMarker')}</span>
            </div>

            <ul
              className="epg__rows"
              data-testid="channel-list"
              style={{
                /*
                 * 띠는 `auto`, 행은 뷰포트에서 낸 높이 — 한 값으로 쓸 수 없어 인라인이다.
                 *
                 * ★ v33-b — **혼합 채널도 한 행의 높이는 같다**(2026-09-22 무대 요청).
                 *   두 칸이라고 행을 키우면 **한 채널이 화면에서 차지하는 몫이 달라져**
                 *   세로축(채널)이 고르지 않게 보인다. 높이는 그대로 두고 **상자를 절반**
                 *   으로 쓴다 — 갈리는 것은 `.epg__track--split` 의 `1fr 1fr` 하나다.
                 */
                gridTemplateRows: cells
                  .map((cell) => (cell.kind === 'band' ? 'auto' : 'var(--epg-row-h)'))
                  .join(' '),
              }}
            >
              {cells.map((cell) =>
                cell.kind === 'band' ? (
                  <li key={cell.key} className="epg__band">
                    {t(cell.key)}
                  </li>
                ) : (
                  <EpgChannelRow
                    inLive={inLive}
                    key={cell.row.channel.channelId}
                    row={cell.row}
                    favorite={isFavorite(favorites, cell.row.channel.channelId)}
                    isCurrentChannel={cell.row.channel.channelId === currentChannelId}
                    currentProductId={currentProductId}
                    onToggleFavorite={onToggleFavorite}
                    onPick={onPick}
                    t={t}
                    /*
                      ★ **재생 중인 행에만 값이 간다.** 나머지는 `null`·`0` 이 상수라
                      `memo` 가 적중하고 초당 다시 그려지지 않는다 — 그리고 그것이
                      「안 보는 행은 안 움직인다」의 구현이기도 하다 (절대 규칙 73).
                    */
                    anchorSec={
                      anchor !== null && cell.row.channel.channelId === now?.channelId
                        ? anchor.elapsedSec
                        : null
                    }
                    driftSec={
                      anchor !== null && cell.row.channel.channelId === now?.channelId
                        ? driftSec
                        : 0
                    }
                    /*
                      ★ v28 — **ONAIR 행에는 언제나 지금이 간다** (`docs/57` §7.1).
                      그 행의 상자는 **절대 시각**에서 나오므로 이 값이 바뀌면 저절로
                      흐른다 — 개인화 행처럼 앵커를 잡을 필요가 없고, 「지금 보는
                      채널인가」도 묻지 않는다. **아무도 안 켜도 방송은 가고 있다.**
                    */
                    nowMs={nowMs}
                  />
                ),
              )}
            </ul>
          </div>
        </div>
      </div>

      {signInHint && <p className="epg__signed-out">{t('epg.signedOutHint')}</p>}

      {onBack !== null && (
        <div style={{ marginTop: '0.9rem' }}>
          <button type="button" className="button button--secondary" onClick={onBack}>
            {t('common.watchBroadcast')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 한 채널 = 한 행. **행머리는 고정 열이고 상자는 시간 위에 절대 배치된다.**
 *
 * ★ **별을 상자 안에 못 둔다** — 상자가 `<button>` 이라 그 안의 버튼은 중첩 상호작용
 * 요소이고 리모컨·스크린리더 양쪽에서 동작이 정의되지 않는다 (`docs/51` §8.3).
 *
 * ★★ **`data-channel-id` 는 상자에만 둔다.** 행에도 두면 `querySelector` 가 조상인 행을
 * 먼저 주고, 행에는 핸들러가 없어 **클릭이 아무 일도 안 한다** — 하네스가 「start 0건」으로
 * 실패하는데 **사람이 만지면 멀쩡하다** (`docs/51` §13 의 17).
 */
const EpgChannelRow = memo(function EpgChannelRow({
  row,
  favorite,
  isCurrentChannel,
  currentProductId,
  inLive,
  onToggleFavorite,
  onPick,
  t,
  anchorSec,
  driftSec,
  nowMs,
}: {
  row: EpgRow;
  favorite: boolean;
  isCurrentChannel: boolean;
  currentProductId: string | null;
  /** v33-c. 그 채널이 지금 바이패스인가 — **보고 있지 않은 칸을 죽이는 근거** */
  inLive: boolean;
  onToggleFavorite: (channelId: string) => void;
  onPick: (channelId: string, productId: string, isEntryCard: boolean) => void;
  t: Translator<MessageKey>;
  /** 재생 중인 행이면 앵커의 콘텐츠 초. 아니면 `null` 이고 서버의 이어보기를 쓴다 */
  anchorSec: number | null;
  /** 앵커 뒤로 흐른 벽시계. **안 보는 행에서는 언제나 0 이라 안 움직인다** */
  driftSec: number;
  /** v28. 지금(epoch ms). **ONAIR 행만 이 값으로 흐른다** */
  nowMs: number;
}) {
  const { channel, lineup, onair } = row;
  const isGeneral = channel.kind === 'general';
  const badgeKey = channelBadgeKey(channel);
  /*
   * ★ v28 — **출처가 둘이고 배타적이다** (`docs/57` §7.1).
   *
   * ONAIR 행의 상자는 **절대 시각**에서 나오므로 `shiftSpans` 도 앵커도 없다 — 매 초
   * `nowMs` 가 바뀌면 그 뺄셈이 저절로 왼쪽으로 민다. **분기가 아니라 정의로 갈린다.**
   */
  const isOnair = channel.delivery === 'onair';
  const baseSpans = isOnair
    ? onairSpansAt(onair?.slots ?? [], nowMs)
    : lineup === null
      ? []
      : shiftSpans(
          rowSpans(row.adPolicy, lineup.items, anchorSec ?? lineup.resumePositionSec),
          driftSec,
        );
  /*
   * ★ v33-b — **한 행이 위아래로 갈린다** (`docs/67` §9 개정 · 절대 규칙 83·85).
   *
   * 녹화 상자는 「지금」 기준 초라 **재생 중일 때만 흐르고**, 라이브 상자는 절대 시각이라
   * **언제나 흐른다** — v27 의 좌표계에 `if` 가 하나도 안 붙는다. 그 둘은 **섞이지 않고
   * 나란히 선다**: 시청자가 라이브를 무시하고 녹화 회차를 고를 수 있으므로(규칙 85)
   * 같은 시각에 **두 지점이 동시에 참**이고, 한 줄로는 둘 중 하나밖에 말할 수 없다.
   *
   * **「지금」 선 하나가 두 칸을 함께 지난다** — 그것이 시청자가 보는 두 지점이다.
   *
   * **ONAIR 행에는 오지 않는다** — 그 채널에는 라이브 이벤트가 없다(한 채널이 두 시각축을
   * 갖지 않는다).
   */
  const liveSpans = isOnair ? [] : liveSpansAt(row.liveEvents, nowMs);
  /*
   * ★ v33-c — **보고 있지 않은 칸을 죽인다** (2026-09-22 무대 신고).
   *
   * 두 칸은 **둘 다 「지금」 선을 지난다** — 그것이 이 판의 요점이고(§9 개정), 그래서
   * **상자의 자리로는 「지금 무엇을 보고 있는가」가 갈리지 않는다.** 무대에서 「둘 다
   * 활성이라 무엇을 보는지 모르겠다」로 났다.
   *
   * **지금 보고 있는 채널에서만 가른다** — 다른 행에서는 아무것도 재생되고 있지 않으므로
   * (절대 규칙 73 ②) 죽일 것이 없다.
   */
  const idleLane = !isCurrentChannel ? null : inLive ? 'recorded' : 'live';
  /** 이 채널이 혼합 채널인가. **보이는 창이 아니라 채널의 성질로 가른다** — 창이 화면
   *  밖으로 나갈 때마다 행 높이가 튀면 그것이 더 나쁘다. */
  const isHybrid = !isOnair && row.liveEvents.length > 0;
  const spans = baseSpans;
  /** ONAIR 행에서 지금 방송 중인 상자. **`index 0` 이 아니다** — 왼쪽이 과거로 차 있다 */
  const onairNow = isOnair ? onairNowIndex(spans) : -1;

  return (
    /*
     * ★★ **지금 보고 있는 채널은 행이 말한다** (2026-09-05 무대 신고).
     *
     * 상자의 하이라이트(`aria-current`)로는 **갈리지 않는다** — 그 표시는 뜻이 둘이기
     * 때문이다: 개인화 행에서는 「내가 보고 있는 회차」이고, **ONAIR 행에서는 「그 채널에서
     * 지금 방송 중인 것」**이라 **아무도 안 보고 있어도 켜진다.** 화면에 ONAIR 채널이 둘
     * 있으면 붉은 상자가 셋이고, 그중 어느 것이 나인지 알 방법이 없었다.
     *
     * 그래서 **채널 축의 사실은 채널 축(행머리)에** 둔다. 상자는 시간 축의 사실 그대로다.
     *
     * ★ **첫 진입(게이트)에서는 표시하지 않는다** — 그때는 보고 있는 채널이 없고,
     * `currentChannelId` 가 `null` 이라 `isCurrentChannel` 이 언제나 거짓이다.
     */
    <li
      className={`epg__row${isCurrentChannel ? ' epg__row--current' : ''}`}
      data-testid="channel-item"
      data-channel-kind={channel.kind}
      /* v28. 하네스가 두 종류를 이것으로 가른다 — `kind` 만으로는 ONAIR 가 안 갈린다 */
      data-channel-delivery={channel.delivery}
      data-current-channel={isCurrentChannel ? 'true' : undefined}
    >
      <div className="epg__head">
        <button
          type="button"
          className="epg__fav"
          data-testid="epg-fav"
          data-favorite-channel-id={channel.channelId}
          aria-pressed={favorite}
          onClick={() => {
            onToggleFavorite(channel.channelId);
          }}
        >
          {/* 별은 그림이고, 읽히는 것은 그 옆의 글자다 — 3m 판정 (`docs/51` §6.4) */}
          <span className="epg__star" aria-hidden="true">
            {favorite ? '★' : '☆'}
          </span>
          {/* v26 — 채널 번호 (절대 규칙 71). 등록된 값이라 번역 대상이 아니다 */}
          <span
            className="epg__number"
            data-i18n="data"
            aria-label={t('epg.channelNumber', { n: channel.number })}
          >
            {channel.number}
          </span>
          {/* 채널명은 **등록된 값이라 번역하지 않는다.** 표시는 이 `<span>` 하나에만 —
              버튼에 붙이면 훑기가 조상까지 면제해 `epg.favorite` 누락이 조용해진다. */}
          <span className="channels__name" data-i18n="data">
            {channel.name}
          </span>
          <span className="epg__sr">{t('epg.favorite')}</span>
        </button>
        {/*
          종류와 개수. **v25 의 행머리가 갖던 것을 그대로 물려받는다** — 시간축이 되면서
          한 번 빠뜨렸고 `channels.listsWhenMany` 가 그것을 잡았다(2026-09-03).
          배지만 가르고 여기를 두면 「드라마 · 상품 4개」로 읽힌다 (절대 규칙 49).
        */}
        <span className="channels__count">
          {/*
            ★ **글자로 말한다.** 3m 밖에서 색만으로는 안 갈리고(`docs/51` §6.4 가 별에
            대해 세운 그 판단), 색약이면 아예 안 갈린다. 왼쪽 띠는 곁들이는 것이다.
          */}
          {isCurrentChannel && <span className="epg__watching">{t('epg.watching')}</span>}
          {/* v33-g. 일반 채널은 **장르**가 배지다 — 없으면 그리지 않는다(`channel-badge.ts`) */}
          {badgeKey !== null && <span className="channels__kind">{t(badgeKey)}</span>}
          {isGeneral
            ? t('channels.episodeCount', { n: channel.itemCount })
            : t('channels.itemCount', { n: channel.itemCount })}
        </span>
      </div>

      <div className={`epg__track${isHybrid ? ' epg__track--split' : ''}`}>
        {/*
          ★ v33-b — **위 칸: 라이브** (`docs/67` §9 개정). 절대 시각이라 저절로 흐르고,
          아래 칸의 녹화 좌표에는 **손대지 않는다.** 혼합 채널에서만 선다.

          **시작 전에는 누를 수 없다** — 미래를 고를 수 없다는 것이 라이브의 성질이고,
          ONAIR 의 미래 상자와 **같은 근거·같은 `disabled`** 다(규칙 29 가 포커스 후보에서
          빼 준다). 진행 중이면 그 조작은 **채널 전환**이다 — 들어가면 리시버가 다음 틱에
          바이패스로 합류한다.
        */}
        {isHybrid && (
          <div
            className={`epg__lane epg__lane--live${idleLane === 'live' ? ' epg__lane--idle' : ''}`}
            data-testid="epg-lane-live"
            data-lane-idle={idleLane === 'live' ? 'true' : undefined}
          >
            {/* 2026-10-29 · 라이브 앞뒤 광고의 띠 — 상자가 아니다(누를 수 없다 · `docs/45` §18). 광고 시간 표시를 끄면 감춘다 */}
            {liveSpans.flatMap((live) =>
              liveAdStripes(live, row.adPolicy?.liveBeforeSec ?? 0, row.adPolicy?.liveAfterSec ?? 0).map((stripe) => (
                <span
                  key={`${live.slot.eventId}:${stripe.kind}`}
                  className={`epg__break epg__break--live epg__break--${stripe.kind}`}
                  data-testid="epg-live-ad"
                  data-live-ad={stripe.kind}
                  style={{ left: `${String(stripe.leftPx)}px`, width: `${String(stripe.widthPx)}px` }}
                  aria-hidden="true"
                />
              )),
            )}
            {liveSpans.map((live) => (
              <EpgLiveBox
                key={live.slot.eventId}
                channelId={channel.channelId}
                live={live}
                running={isLiveOpen(live.phase)}
                onPick={() => {
                  // **회차를 가리키지 않는다** — 라이브에는 항목이 없다(절대 규칙 68 의 단서).
                  onPick(channel.channelId, '', true);
                }}
                t={t}
              />
            ))}
          </div>
        )}
        <div
          className={`${isHybrid ? 'epg__lane epg__lane--recorded' : 'epg__lane epg__lane--only'}${
            idleLane === 'recorded' ? ' epg__lane--idle' : ''
          }`}
          data-testid={isHybrid ? 'epg-lane-recorded' : undefined}
          data-lane-idle={idleLane === 'recorded' ? 'true' : undefined}
        >
        {isOnair ? (
          /*
           * ★ **ONAIR 행** (`docs/57` §7.3). 누를 수 있는 것은 **「지금」 상자 하나**다 —
           * 미래는 고를 수 없고, 과거는 이미 지나갔다. 그것이 이 채널의 성질이고
           * 화면이 그것을 말한다.
           *
           * **`disabled` 다.** 포커스 후보가 `button:not([disabled])` 라(절대 규칙 29)
           * 리모컨이 저절로 건너뛰고, 행마다 「지금」 하나는 언제나 활성이라 **포커스가
           * 갈 곳이 없어지는 일은 없다.**
           */
          onair === null ? (
            [0, 1, 2].map((i) => (
              <span
                key={i}
                className="epg__box epg__box--placeholder"
                aria-hidden="true"
                style={{
                  left: `${String(xOf(i * 300))}px`,
                  width: `${String(xOf(300) - xOf(0))}px`,
                }}
              />
            ))
          ) : (
            spans.map((span, n) => {
              const slot = onair.slots[span.index];
              if (slot === undefined) return null;
              return (
                <EpgOnairBox
                  key={`${String(slot.startAtMs)}:${String(n)}`}
                  channelId={channel.channelId}
                  slot={slot}
                  span={span}
                  live={n === onairNow}
                  onPick={() => {
                    // 「지금」 상자만 누를 수 있다 — 그 조작은 **채널 전환**이다.
                    onPick(channel.channelId, slot.productId, true);
                  }}
                  t={t}
                />
              );
            })
          )
        ) : lineup === null
          ? /* 편성표를 아직 못 받았다. **key 를 인덱스로 잡아** 진짜 상자가 올 때
               언마운트되지 않게 한다 — 언마운트되면 포커스가 `<body>` 로 떨어지고,
               **로컬 서버는 빨라서 그 창이 안 보인다.** */
            [0, 1, 2].map((i) => (
              <span
                key={i}
                className="epg__box epg__box--placeholder"
                aria-hidden="true"
                style={{ left: `${String(xOf(i * 300))}px`, width: `${String(xOf(300) - xOf(0))}px` }}
              />
            ))
          : spans.map((span, n) => {
              const item = lineup.items[span.index];
              if (item === undefined) return null;
              return (
                <EpgBox
                  key={`${String(span.cycle)}:${String(span.index)}:${String(n)}`}
                  channelId={channel.channelId}
                  item={item}
                  span={span}
                  isGeneral={isGeneral}
                  resumePositionSec={lineup.resumePositionSec}
                  /*
                    ★ **되돌아온 바퀴는 「지금 재생 중」이 아니다** (2026-09-03, 하네스
                    결과를 읽다가 잡았다 — `epg.currentRowAnchorsAtPlaying` 이 「지금
                    재생 중」을 **여섯 번** 뱉고 있었다). 상품 id 만 보면 같은 상품의
                    **모든 바퀴**가 참이 되어 한 행이 「지금 재생 중」을 여섯 번 주장하고,
                    `aria-current` 도 여섯 개가 된다. **검사는 통과하고 있었다.**
                  */
                  /*
                    ★ v33-c. **바이패스 중에는 녹화 상자가 「지금 재생 중」이 아니다.**
                    `getCurrentItem()` 은 바이패스 중에도 녹화 회차를 돌려주므로(규칙 83)
                    이 판정만 두면 **라이브와 녹화가 동시에 「지금」을 주장한다.**
                  */
                  nowPlaying={
                    isCurrentChannel &&
                    !inLive &&
                    span.cycle === 0 &&
                    item.productId === currentProductId
                  }
                  onPick={() => {
                    onPick(channel.channelId, item.productId, isEntry(span));
                  }}
                  t={t}
                />
              );
            })}
        </div>
      </div>
    </li>
  );
});

/**
 * ONAIR 행의 상자 하나 (v28, `docs/57` §7.3).
 *
 * ── `EpgBox` 와 갈리는 것 넷 ────────────────────────────────
 *
 * ① **「지금」이 `index 0` 이 아니다** — 창의 왼쪽이 과거로 차 있다
 * ② **미래·과거는 `disabled`** — 고를 수 없는 것이 이 채널의 성질이다
 * ③ **이어보기 표시가 없다** — 두고 온 자리가 없다 (절대 규칙 76)
 * ④ **「이미 본 구간」(`seenPx`)을 그리지 않는다** — 왼쪽이 찬 것은 **내가 본 것이
 *    아니라 내가 못 본 것**이고, 같은 그림을 쓰면 그 대비가 통째로 거짓말이 된다
 *    (`docs/57` §1.1 의 ▒ 와 ░)
 */
function EpgOnairBox({
  channelId,
  slot,
  span,
  live,
  onPick,
  t,
}: {
  channelId: string;
  slot: OnairSlot;
  span: EpgSpan;
  live: boolean;
  onPick: () => void;
  t: Translator<MessageKey>;
}) {
  const geo = boxGeometry(span);
  if (geo === null) return null;

  const whenText = live
    ? t('epg.nowPlaying')
    : span.startSec > 0
      ? t('epg.startsInAbout', { n: Math.max(1, Math.round(span.startSec / 60)) })
      : t('onair.past');

  return (
    <button
      type="button"
      className={`epg__box${live ? '' : ' epg__box--past'}`}
      data-testid="epg-card"
      data-channel-id={channelId}
      data-product-id={slot.productId}
      /*
       * ★ **상자의 신원** (v28, 2026-09-05). 한 바퀴가 돌면 `data-product-id` 가 다시
       * 나오므로 그것만으로는 **같은 상자인지** 말할 수 없다. 흐름을 재려면 앞뒤 두
       * 표본에서 같은 상자를 집어야 하는데, 왼쪽 끝의 상자는 창 밖으로 나가며 **잘려
       * 나가므로** `boxes[0]` 은 매번 다른 상자다 — 그것을 재면 **흐르는데 0px 이 나온다.**
       */
      data-slot-start={String(slot.startAtMs)}
      data-onair-live={live ? 'true' : undefined}
      /* ★ 「지금」 하나만 누를 수 있다 — 나머지는 포커스 후보에서도 빠진다(규칙 29) */
      disabled={!live}
      aria-current={live ? 'true' : undefined}
      style={{ left: `${String(geo.leftPx)}px`, width: `${String(geo.widthPx)}px` }}
      onClick={onPick}
    >
      {/*
        v38 — **브레이크를 제자리에.** 서버가 이어 놓아 시각이 확정된 광고 구간이다(6번 봉합 · 7번 CSPM 슬레이트) —
        회차 안 중간 광고도 꼬리가 아니라 그 자리다(`OnairSlot.breaks`).
      */}
      {geo.breaks.map((b) => (
        <span
          key={b.leftPx}
          className="epg__break"
          data-testid="epg-break"
          style={{ left: `${String(b.leftPx)}px`, width: `${String(b.widthPx)}px` }}
          aria-hidden="true"
        />
      ))}
      <span className="epg__box-body">
        <span className="epg__box-title" data-i18n="data">
          {slot.title}
        </span>
        {boxDensity(geo.widthPx) === 'titleAndLabel' && slot.episodeNo !== null && (
          <span className="epg__box-label" data-i18n="data">
            {t('episodes.label', { season: slot.seasonNo ?? 1, episode: slot.episodeNo })}
          </span>
        )}
      </span>
      <span className="epg__sr" data-testid="epg-when">
        {whenText}
      </span>
    </button>
  );
}

/**
 * v33. 라이브 이벤트 상자 (`docs/67` §9.3).
 *
 * ★ **`data-product-id` 가 없다.** 라이브에는 항목이 없다 — 상자의 신원은 `eventId` 다.
 * 하네스가 흐름을 잴 때 이 값으로 같은 상자를 집는다(ONAIR 가 `data-slot-start` 로 한 일).
 */
function EpgLiveBox({
  channelId,
  live,
  running,
  onPick,
  t,
}: {
  channelId: string;
  live: EpgLiveSpan;
  running: boolean;
  onPick: () => void;
  t: Translator<MessageKey>;
}) {
  const { locale } = useLocale();
  const geo = boxGeometry({
    index: 0,
    cycle: 0,
    startSec: live.startSec,
    endSec: live.endSec,
    breakSec: 0,
  });
  if (geo === null) return null;

  /*
   * ★ v34. **큐 상태를 글자로 말한다** (`docs/69` §7.2 · 명세서 [0178] · 도 17).
   *
   * - 개시 대기 — 예정은 지났고 부조정실이 아직 안 눌렀다. 시작단이 지금 선에 붙어 간다
   * - 연장 중 — 예상 종료를 지났고 종료 큐를 기다린다. 끝단이 지금 + 5분에 붙어 간다
   * - 예상 종료 HH:MM — 끝이 큐로 정해지는 창. **남은 시간을 세지 않는다** — 줄어드는 초는
   *   확정된 약속처럼 보여, 연장되면 시청자를 헷갈리게 한다([0139] · 바이패스 화면의 방침과 같다)
   */
  const note =
    live.phase === 'waiting'
      ? t('epg.live.waiting')
      : live.phase === 'extended'
        ? t('epg.live.extended')
        : live.openEnd && live.phase !== 'ended'
          ? t('epg.live.expectedEnd', { time: formatTimeOfDay(live.slot.endAtMs, locale) })
          : null;

  return (
    <button
      type="button"
      className={`epg__box epg__box--live${running ? ' is-running' : ''}${
        live.phase === 'waiting' ? ' is-waiting' : ''
      }${live.openEnd && live.phase !== 'ended' ? ' is-open-end' : ''}`}
      data-live-phase={live.phase}
      data-testid="epg-live"
      data-channel-id={channelId}
      data-event-id={live.slot.eventId}
      data-live-running={running ? 'true' : undefined}
      /* 시작 전에는 누를 수 없다 — 미래를 고를 수 없다(ONAIR 의 그 근거와 같다) */
      disabled={!running}
      aria-current={running ? 'true' : undefined}
      style={{ left: `${String(geo.leftPx)}px`, width: `${String(geo.widthPx)}px` }}
      onClick={onPick}
    >
      <span className="epg__box-body">
        <span className="epg__box-title" data-i18n="data">
          {t('epg.liveBadge')} {live.slot.title}
        </span>
        {note !== null && (
          <span className="epg__box-note" data-testid="epg-live-note">
            {note}
          </span>
        )}
      </span>
      <span className="epg__sr" data-testid="epg-when">
        {running
          ? t('epg.nowPlaying')
          : live.phase === 'waiting'
            ? t('epg.live.waiting')
            : t('epg.startsInAbout', { n: Math.max(1, Math.round(live.startSec / 60)) })}
      </span>
    </button>
  );
}

/**
 * **「지금」 상자인가.** 회전된 배열의 `cycle 0 · index 0` 이다 (절대 규칙 68 의 v27 단서).
 *
 * ★ **화면 위치로 판정하지 않는다** — 눈금 반올림 때문에 경계에서 흔들리고, 그러면
 * 어떤 날은 「지금」 상자가 `selectItem()` 을 타서 **이어보기 지점이 0 으로 덮인다.**
 */
function isEntry(span: LineupSpan): boolean {
  return span.cycle === 0 && span.index === 0;
}

/**
 * 상자 하나. **보이는 글은 제목과(넓으면) 회차 정보뿐이고, 시간을 말하는 글은 감춰 둔다.**
 *
 * ★ 감춘 `<span>` 이 없으면 **시각장애 시청자에게 시간이 통째로 사라진다.** 그리고
 * `aria-label` 에 제목을 넣지 않는다 — **한글 훑기가 `aria-label` 도 보므로** 등록값이
 * 거기 들어가면 번역 누락으로 신고된다 (`docs/51` §13 의 22).
 */
function EpgBox({
  channelId,
  item,
  span,
  isGeneral,
  resumePositionSec,
  nowPlaying,
  onPick,
  t,
}: {
  channelId: string;
  item: LineupItem;
  span: LineupSpan;
  isGeneral: boolean;
  resumePositionSec: number | null;
  nowPlaying: boolean;
  onPick: () => void;
  t: Translator<MessageKey>;
}) {
  const geo = boxGeometry(span);
  // 창 밖이면 **그리지 않는다** — 폭 0 짜리 포커스 후보가 생기면 링이 사라진다.
  if (geo === null) return null;

  const entry = isEntry(span);
  const showResume = entry && resumePositionSec !== null;
  const whenText = nowPlaying
    ? t('epg.nowPlaying')
    : showResume
      ? t('episodes.resumeAt', { time: formatClock(resumePositionSec) })
      : entry
        ? t('epg.entryPoint')
        : t('epg.startsInAbout', { n: Math.max(1, Math.round(span.startSec / 60)) });

  return (
    <button
      type="button"
      className="epg__box"
      data-testid="epg-card"
      /* `data-channel-id` 는 **상자에만** 둔다 — 위 ★★ */
      data-channel-id={channelId}
      data-product-id={item.productId}
      data-cycle={span.cycle}
      data-entry-card={entry ? 'true' : undefined}
      aria-current={nowPlaying ? 'true' : undefined}
      style={{ left: `${String(geo.leftPx)}px`, width: `${String(geo.widthPx)}px` }}
      onClick={onPick}
    >
      {geo.seenPx > 0 && (
        <span className="epg__seen" style={{ width: `${String(geo.seenPx)}px` }} aria-hidden="true" />
      )}
      {geo.breaks.map((b) => (
        <span
          key={b.leftPx}
          className="epg__break"
          style={{ left: `${String(b.leftPx)}px`, width: `${String(b.widthPx)}px` }}
          aria-hidden="true"
        />
      ))}
      <span className="epg__box-body">
        <span className="epg__box-title" data-i18n="data">
          {item.title}
        </span>
        {/* 회차 정보는 **폭이 문턱을 넘을 때만** (`docs/55` §1.3 · D18) */}
        {boxDensity(geo.widthPx) === 'titleAndLabel' && labelLine(t, item, isGeneral)}
      </span>
      {geo.seenPx > 0 && <span className="epg__sr">{t('epg.seenPart')}</span>}
      <span className="epg__sr" data-testid="epg-when">
        {whenText}
      </span>
      {span.cycle > 0 && <span className="epg__sr">{t('epg.repeats')}</span>}
    </button>
  );
}

/**
 * 둘째 줄 — 일반 채널은 회차 라벨, 홈쇼핑은 분류 (절대 규칙 49 · 같은 자리 다른 어휘).
 *
 * ★ **일반 채널에서 `episodeNo` 가 없으면 아무것도 안 그린다.** v25 의 `episodeLabel()`
 * 은 그 경우 `meta.category`(등록값)를 돌려주는데, 사전을 거치는 `<span>` 에는
 * `data-i18n="data"` 가 없어 **한글 훑기가 그것을 번역 누락으로 신고한다.** 시드의 모든
 * 회차에 `episodeNo` 가 있어 **v25 에서는 한 번도 안 났다** (`docs/55` §1.3 의 ★).
 */
function labelLine(t: Translator<MessageKey>, item: LineupItem, isGeneral: boolean) {
  if (!isGeneral) {
    return (
      <span className="epg__box-label" data-i18n="data">
        {item.meta.category}
      </span>
    );
  }
  const episode = episodeNoOf(item);
  if (episode === null) return null;
  const season = item.content?.seasonNo ?? null;
  return (
    <span className="epg__box-label">
      {season === null
        ? t('episodes.labelNoSeason', { episode })
        : t('episodes.label', { season, episode })}
    </span>
  );
}
